/*
 * 戒导航 · 分享模块 前台「分享」入口按钮
 * =============================================================================
 * 设计依据：doc/分享功能设计方案.md
 *   · §6.1.3「入口清单」—— 本组件同时服务第 1、2、3 三处入口：
 *       ① 讨论页顶部控件区（DiscussionControls.userControls 下拉项）
 *       ② 每个楼层右下控件区（PostControls.userControls 下拉项）
 *       ③ 博客文章页正文下方（BlogItem.articleItems 插 share@90 的横向常驻分享条）
 *     第 ④ 处（首页 nav.php 内联）**不在本扩展内**，由 nav 扩展的 PHP 模板自行实现。
 *   · §6.4.3「chunk 表」—— 本文件属于**主包**，体积硬约束 ≤ 2 KB（gzip）。
 *     因此这里**只做一件事**：渲染按钮 + 按需加载面板；所有「重」逻辑
 *     （金句抽取、文案模板、渠道渲染、二维码、海报绘制）一律不进主包。
 *   · §6.7.3「诚实边界」—— 桌面端微信/朋友圈点不动属预期行为，面板内会高亮二维码区，
 *     这条降级逻辑在面板 chunk 里，本组件不感知。
 *   · §7.6「无障碍」—— 按钮必须有可读名称（核心 Button 在无 aria-label 且无文本时
 *     会 fireDebugWarning），触控目标 ≥ 44×44px（由 less 保障）。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 关键设计①：为什么用「工厂函数 + 动态 import」而不是静态 import 面板
 * ─────────────────────────────────────────────────────────────────────────────
 * 面板含二维码渲染、色卡、文案模板，代码量大；若静态 import，所有访客（哪怕从不点分享）
 * 都要下载这份代码，直接违反 §6.4.3「面板打开前不下载任何分享代码」。
 * 核心 ModalManagerState.show() 支持传「返回 Promise<{default: Modal}> 的函数」：
 *   见 vendor/flarum/core/js/src/common/states/ModalManagerState.ts L83-91 ——
 *   若入参不是 Modal 子类，会 `await componentClass()` 再取 `.default`。
 * 本文件因此把 `loadPanelModule` 做成**模块级唯一动态 import 站点**，
 * 供「hover/focus 预热」与「点击打开」共用同一个 Promise（webpack 会去重，不会重复请求）。
 *
 * ⚠️ 已知偏差（记入 P1-10 回写清单）：
 *   核心在 chunk 加载期会把 `loadingModal` 置 true 并同步 redraw ⇒ 显示**旋转 spinner**，
 *   这与 §7.4「本面板不做旋转 spinner」相冲突。缓解办法是预热（见 warmup()）：
 *   桌面端鼠标移到按钮上、或键盘 Tab 聚焦时就开始拉 chunk，使「第一次点开」也接近瞬开。
 *   若 P1-10 实测仍明显可见 spinner，再考虑改用自己的加载遮罩（那时需绕开 app.modal.show）。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 关键设计②：依赖注入（resolve / config）而不是反向 import ../index
 * ─────────────────────────────────────────────────────────────────────────────
 *   · `resolve()` 由挂载方（index.ts）提供，是「延迟取值闭包」——只在**点击那一刻**
 *     才去读 discussion/post/article 模型。这样 ShareButton 自身不必静态 import
 *     utils/text.ts（那会把一整份文案抽取逻辑拖进主包，撑爆 2 KB 预算）。
 *   · `config` 由挂载方注入，而不是组件内 `import { shareConfig } from '../index'`
 *     —— 后者会形成「index → ShareButton → index」的循环依赖。
 *   · 两个类型都用 `import type`：babel 擦除后**不产生任何运行时 import**，
 *     既不会拖代码，也不会命中 webpack 的 autoChunkNameLoader 正则。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 红线（本项目扩展通用约定，见 packages/media、packages/icon-picker 等先例）
 * ─────────────────────────────────────────────────────────────────────────────
 *   1. **不 import mithril**：JSX 由 flarum-webpack-config 以 `pragma: 'm'` 编译为
 *      对全局标识符 `m` 的调用；一旦 import mithril 会打进第二份 mithril，vnode 体系不一致。
 *   2. **注释里不能出现动态 import 的字面量写法**：webpack 的 autoChunkNameLoader.cjs
 *      用正则 `/^.*import\(['"].*['"]\).*$/gm` 扫描全部源码行，**不区分注释与代码**。
 *      因此本文件的注释一律用「动态 import」中文表述，不写代码形式。（真实代码可以写，
 *      vendor 与项目内已有先例。）
 *   3. app.modal.show 的第三个参数 `stackModal` **必须显式传 true**：默认 false 会
 *      清空弹窗栈（把当前弹窗顶掉），传 true 才是「叠加一层」。
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
// ⚠️ 这是**类型导入**，babel 擦除后不产生任何运行时 import（与「不 import mithril」的红线不冲突，
//    它不会把 mithril 打进产物）。项目内先例：packages/media/js/src/forum/components/BlogCoverCropperModal.tsx L31。

// ⚠️ 只导类型，不导实现：babel 会整行擦除，主包不会因这两行增加任何体积。

/**
 * 面板模块加载器（**本文件唯一的动态 import 站点**）。
 *
 * 返回的 Promise 会被 webpack 缓存：预热与点击打开拿到的是**同一个** Promise，
 * 所以预热之后点击不会产生第二次网络请求，只是 `await` 一个已 resolve 的 Promise。
 * 这也意味着「面板 chunk 加载失败」的状态会被记住 —— 见 warmup() 里的失败自愈处理。
 */
const loadPanelModule = () => import(/* webpackChunkName: 'forum/components/SharePanelModal', webpackMode: 'lazy-once' */ './SharePanelModal');

/**
 * 按钮形态。
 *
 *   · `control` —— 讨论页 / 楼层的**下拉控件项**。与核心其它下拉项一致：图标 + 文案。
 *     之所以不叫 'icon'：核心 DiscussionControls 里所有下拉项都带文案
 *     （见 vendor/.../forum/utils/DiscussionControls.js 的 reply/edit 等），
 *     纯图标项会与相邻项风格割裂；这里保留文案，图标由其负责识别。
 *   · `bar`     —— 博客文章页正文下方的**横向常驻分享条**（§6.1.3 第 3 处）。
 *     博客是长文阅读场景，需要一个「就在眼前」的显眼入口，因此另一套样式与尺寸。
 */

/** 本组件属性。 */

/**
 * 前台「分享」入口按钮。
 *
 * 职责边界（刻意保持极窄，以守住主包 ≤ 2 KB 的预算）：
 *   ✔ 渲染按钮、处理点击、防重入、按需加载面板 chunk
 *   ✘ 不做金句抽取、不做文案模板、不做渠道渲染、不画海报 —— 全部在面板 chunk 内
 */
export default class ShareButton extends Component {
  /**
   * 打开流程进行中标记（防重入）。
   *
   * 为什么必须有：chunk 首次加载是异步的（几百毫秒）。这段时间里按钮**还没有被禁用**，
   * 用户完全可能连点两三次。若不做拦截，`app.modal.show` 会被调用多次，
   * 弹窗栈里就会叠出好几个一模一样的面板（用户点关闭只关掉最上面一层，体验很怪）。
   */
  opening = false;

  /** 预热是否已发起（避免 hover/focus 反复触发加载）。 */
  warmed = false;
  view() {
    const variant = this.attrs.variant ?? 'control';

    /*
     * 文案来源：locale 的 `jiedh-share.forum.share`（P1-8e 补齐）。
     * 带参数的 trans() 会返回数组，必须过 extractText()，故统一走 trans() 包装。
     */
    const label = trans('share');

    /*
     * 关于 attrs 的透传（已核对 vendor/flarum/core/js/src/common/components/Button.tsx L72-113）：
     *   · Button 只解构走 type / aria-label / icon / noStyleOverride / disabled /
     *     loading / className / class / helperText 这 9 个具名字段，**其余全部 `...attrs`
     *     展开到 <button> 元素上** ⇒ onmouseenter / onfocus 可直接作为 attrs 传入，
     *     不需要自己写 oncreate + addEventListener。
     *   · `disabled || loading` 时核心会 `delete attrs.onclick`，禁用即不可点，无需自行判断。
     *   · 传进来的 aria-label 若是翻译对象，核心会自动 extractText()；这里已提前 extract，
     *     传字符串同样正确。
     *   · 有 icon 时核心自动加 `has-icon` 类（L98-103），less 可据此做图标/文案间距。
     */
    return m(Button, {
      icon: "fas fa-share-nodes",
      className: `jiedh-ShareButton jiedh-ShareButton--${variant}`,
      "aria-label": label,
      onclick: this.openPanel.bind(this),
      onmouseenter: this.warmup.bind(this),
      onfocus: this.warmup.bind(this)
    }, label);
  }

  /**
   * 预热：提前把面板 chunk 拉下来（**不打开面板**）。
   *
   * 触发时机（由 view() 绑到 <button> 上）：
   *   · `onmouseenter` —— 桌面端鼠标移到按钮上，正常用户从「悬停」到「点下」有
   *     100ms 以上的间隔，足以覆盖一次内网 chunk 请求；
   *   · `onfocus` —— 键盘用户 Tab 到按钮时同样触发，兼顾无障碍路径。
   *
   * 为什么需要它：核心的异步 modal 在 chunk 加载期会显示旋转 spinner（见文件头「已知偏差」），
   * 预热能让「实际点击」时 Promise 已 resolve，视觉上接近瞬开。
   * 移动端没有 hover，首次点击仍可能看到极短 spinner —— 这是可接受的降级。
   */
  warmup() {
    if (this.warmed) return;
    this.warmed = true;
    loadPanelModule().catch(error => {
      /*
       * 预热失败**不打扰用户**（此刻用户还没点，弹错误提示很突兀），只记日志，
       * 并把 warmed 复位 —— 让下一次 hover/focus 或真正点击时能重试。
       * 注意：webpack 对同一个 import() 会复用同一个 Promise，所以「复位重试」
       * 实际不会重新发请求；真正的补救是用户点击时走 openPanel() 的失败分支给出可见提示。
       */
      this.warmed = false;
      console.warn('[jiedh-share] 分享面板 chunk 预热失败（点击时仍会重试）：', error);
    });
  }

  /**
   * 点击「分享」：取源数据 → 打开面板。
   *
   * 执行顺序刻意如此，不要调整：
   *   1. `opening` 防重入 —— 必须最先判断，且**同步**置位（chunk 是异步的，中间会让出事件循环）；
   *   2. `resolve()` 取值 —— 取值失败（null）就直接返回，绝不打开一个空面板；
   *   3. `app.modal.show(...)` —— 传工厂函数（异步加载）+ `stackModal = true`（叠加而非顶替）。
   */
  async openPanel() {
    // ① 防重入：见字段 opening 的说明。
    if (this.opening) return;
    this.opening = true;
    try {
      // ② 取当前要分享的对象。resolve 内部只读模型、不做网络请求，因此这里是同步完成的。
      const source = this.attrs.resolve();
      if (!source) {
        console.warn('[jiedh-share] 点击分享时未能取得分享对象（模型可能尚未加载）：已忽略本次点击。');
        return;
      }

      /*
       * ③ 打开面板。
       *   · 第一参数传 `loadPanelModule`（工厂函数）而非直接传组件类：
       *     核心 ModalManagerState.show() 会 await 它并取 `.default`
       *     —— 这正是「面板 chunk 按需加载」的实现路径（§6.4.3）。
       *   · `as any` 的原因：TypeScript 看到的是 `() => Promise<typeof import(...)>`，
       *     而 show() 的 AsyncModalClass 类型在核心内部标注得更窄；本项目 js 目录
       *     无 tsconfig、不做类型检查，此处断言仅为消除编辑器红波浪线，运行时无影响。
       *   · 第三参数 `true` = stackModal。**默认的 false 会把当前弹窗栈整个清空**
       *     （例如从某个已打开的弹窗里点分享，会把那个弹窗顶掉），必须显式传 true。
       */
      await app.modal.show(loadPanelModule, {
        source,
        config: this.attrs.config
      }, true);

      // 面板正常打开；此处在面板关闭后才会 resolve，因此把位置留给 finally 复位。
    } catch (error) {
      /*
       * chunk 加载失败 / 面板构造抛错。这是**用户已表达意图**的场景，必须给可见反馈，
       * 故用 alert 而不是只打日志；文案走 locale（含「请检查网络后重试」的引导）。
       */
      console.error('[jiedh-share] 打开分享面板失败：', error);
      alert(trans('panel_open_failed'));
    } finally {
      // 无论成功失败都要复位，否则一次异常之后按钮就永远点不动了。
      this.opening = false;
    }
  }
}

/**
 * 文案取值助手。
 *
 * 统一 `jiedh-share.forum.` 前缀，避免每个调用点重复拼写。
 *
 * ★ 必须过 extractText()：带参数时 Translator.trans() 走 ICU rich 分支返回**数组**
 *   （例如 ['还剩 ', 3, ' 次']），直接字符串拼接会得到垃圾结果。
 *   这里虽然当前只用到无参键，但保持与其它组件同一写法，后续加参数不会踩坑
 *   （同一写法见 packages/media/js/src/forum/components/BlogCoverCropperModal.tsx L44-45）。
 */
function trans(key, params) {
  if (params === void 0) {
    params = {};
  }
  return extractText(app.translator.trans('jiedh-share.forum.' + key, params));
}
flarum.reg.add('jiedh-share', 'forum/components/ShareButton', ShareButton);flarum.reg.addChunkModule('556', '843', 'jiedh-share', 'forum/components/SharePanelModal');
flarum.reg.addChunkModule('556', '843', 'jiedh-share', 'forum/utils/palettes');
flarum.reg.addChunkModule('556', '843', 'jiedh-share', 'forum/utils/channels');
flarum.reg.addChunkModule('556', '843', 'jiedh-share', 'forum/utils/browser');